import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  Divider,
  Zoom,
  LinearProgress,
  Chip,
  Popover,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SendIcon from "@mui/icons-material/Send";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError, checkBeforeOutbound } from "../../utils/subscriptionError";
import { getAuthToken } from "../../utils/getAuthToken";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { ReactComponent as EmojiButton } from "../../Images/Inbox/ChatBox/emojiButton.svg";
import { ReactComponent as GalleryButton } from "../../Images/Inbox/ChatBox/Gallery.svg";
import { ReactComponent as RecordingButton } from "../../Images/Inbox/ChatBox/RecordingButton.svg";
import AudioRecorder from "../Inbox/Features/ChatBoxFeatures/AudioRecorder.jsx";
import { formatDateSeparator } from "../../utils/timestampUtils";
import {
  fetchTaskChatMessages,
  fetchOlderTaskChatMessages,
  TASK_CHAT_PAGE_SIZE,
  sendTaskChatMessage,
  uploadTaskChatMedia,
  markTaskChatReadThunk,
} from "../../redux/Slice/Tasks/taskChatSlice";
import TaskChatMessageMedia from "./TaskChatMessageMedia";
import { classifyTaskChatComposerFile, taskChatComposerFileAccept } from "../../utils/taskChatComposerFiles";
import { nameToEmoji } from "../../config/AvatarColor";

const SCROLL_BOTTOM_THRESHOLD_PX = 120;

/**
 * CRM-style colour coding for activity events, inferred from the log text.
 * Returns an accent colour + icon; order matters (recurrence before "complete").
 */
const ACTIVITY_CATEGORIES = [
  { test: /recurr|catch-?up|interval/i, accent: "#f59e0b", Icon: AutorenewIcon },
  { test: /complete|finished|\bdone\b|closed/i, accent: "#10b981", Icon: CheckCircleOutlineIcon },
  { test: /priorit/i, accent: "#ef4444", Icon: LowPriorityIcon },
  { test: /status/i, accent: "#0ea5e9", Icon: FlagOutlinedIcon },
  { test: /\btype\b/i, accent: "#8b5cf6", Icon: CategoryOutlinedIcon },
  { test: /file|attach|upload|document|image|photo/i, accent: "#06b6d4", Icon: AttachFileIcon },
  { test: /deadline|due date|reschedul/i, accent: "#f97316", Icon: EventOutlinedIcon },
];
const classifyTaskActivity = (text) => {
  const s = String(text || "");
  for (const c of ACTIVITY_CATEGORIES) if (c.test.test(s)) return c;
  return { accent: "#64748b", Icon: BoltOutlinedIcon };
};

/**
 * Renders an activity summary with the changed VALUE emphasised (bold), so
 * updates are scannable. Backend phrasings put the new value in "quotes"
 * (status/priority/type/title/filename/checklist item); deadline dates come
 * after "to" unquoted — normalise those into the same quoted path. Newlines are
 * preserved by the caller's `whiteSpace: pre-line`.
 */
// Emphasis for a changed value. User updates get a soft grey tag; automation
// (recurrence) cards get plain bold text (no background), per design.
const activityValueSx = (isAutomation) =>
  isAutomation
    ? {
        fontWeight: 600,
        color: (t) => (t.palette.mode === "dark" ? "#e2e8f0" : "#374151"),
      }
    : {
        fontWeight: 550,
        mx: 0.2,
        px: 0.6,
        py: 0.1,
        borderRadius: 0.75,
        bgcolor: (t) =>
          t.palette.mode === "dark" ? "rgba(148, 163, 184, 0.18)" : "rgba(15, 23, 42, 0.06)",
        color: (t) => (t.palette.mode === "dark" ? "#e2e8f0" : "#374151"),
        boxDecorationBreak: "clone",
        WebkitBoxDecorationBreak: "clone",
      };

const renderActivityText = (raw, isAutomation = false) => {
  let text = String(raw || "");
  if (!text) return "—";
  // Normalise the changed VALUE into a quoted segment so it picks up emphasis.
  // User updates quote the value already ("Completed"); deadlines come after "to";
  // recurrence/automation phrasing uses "Label: value." + parenthetical dates.
  text = text
    .replace(/(deadline to )([^\n".]+)/gi, (_, prefix, date) => `${prefix}"${date.trim()}"`)
    .replace(
      /(Status|Next deadline|Next run|Deadline|Missed interval\(s\)):\s*([^.\n]+)(?=\.)/g,
      (_, label, val) => `${label}: "${val.trim()}"`
    )
    .replace(/unchanged \(([^)]+)\)/g, (_, d) => `unchanged ("${d.trim()}")`);
  const nodes = [];
  const re = /"([^"]+)"/g;
  let last = 0;
  let match;
  let i = 0;
  let firstPlainDone = false;
  // Capitalise label text (first letter of the line + field nouns), never the
  // values, which live inside the quoted segments handled separately.
  const capLabel = (seg) => {
    let s = seg.replace(
      /\b(status|priority|type|title|description|deadlines?|owners?|assignees?|participants?|recurrence|checklists?|files?|items?|intervals?)\b/gi,
      (w) => w.charAt(0).toUpperCase() + w.slice(1)
    );
    if (!firstPlainDone && /\S/.test(s)) {
      s = s.replace(/^(\s*)([a-z])/, (_, sp, ch) => sp + ch.toUpperCase());
      firstPlainDone = true;
    }
    return s;
  };
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) nodes.push(capLabel(text.slice(last, match.index)));
    nodes.push(
      <Box key={`v${i}`} component="strong" sx={activityValueSx(isAutomation)}>
        {match[1]}
      </Box>
    );
    last = match.index + match[0].length;
    i += 1;
  }
  if (last < text.length) nodes.push(capLabel(text.slice(last)));
  return nodes.length ? nodes : text;
};

/** WhatsApp-style doodle wallpaper: small varied doodles scattered, tinted brand green. */
const buildChatWallpaper = (stroke) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 240 240'><g fill='none' stroke='${stroke}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='34' cy='40' r='10'/><circle cx='30' cy='37' r='1.1' fill='${stroke}' stroke='none'/><circle cx='38' cy='37' r='1.1' fill='${stroke}' stroke='none'/><path d='M29 43a6 4 0 0 0 10 0'/><path d='M196 30c-3-6-12-4-12 3 0 5 7 9 12 13 5-4 12-8 12-13 0-7-9-9-12-3z'/><path d='M120 26l2.6 6.4 6.9.5-5.3 4.5 1.7 6.7-5.9-3.7-5.9 3.7 1.7-6.7-5.3-4.5 6.9-.5z'/><path d='M150 96h24a4 4 0 0 1 4 4v12a4 4 0 0 1-4 4h-10l-6 6v-6h-8a4 4 0 0 1-4-4v-12a4 4 0 0 1 4-4z'/><path d='M40 116l5 5 10-12'/><circle cx='210' cy='150' r='7'/><path d='M210 139v-4M210 165v-4M221 150h4M195 150h4M202 142l-3-3M221 161l-3-3M202 158l-3 3M221 139l-3 3'/><path d='M28 182v20M28 182c0-3 16-4 16-7v20'/><circle cx='24' cy='202' r='4'/><circle cx='40' cy='195' r='4'/><path d='M150 176h22v9a8 8 0 0 1-8 8h-6a8 8 0 0 1-8-8z'/><path d='M172 179h3a4 4 0 0 1 0 8h-3'/><path d='M156 168v4M162 166v6M168 168v4'/><circle cx='108' cy='198' r='6'/><circle cx='90' cy='70' r='3'/><circle cx='182' cy='216' r='3'/><path d='M212 96v12M206 102h12'/><path d='M70 150l4 8 4-8'/><path d='M52 26h22v15H52z'/><path d='M52 26l11 8 11-8'/><path d='M104 74l18-7-6 16-4-6z'/><circle cx='118' cy='120' r='8'/><path d='M118 120v-4M118 120l3 2'/><circle cx='62' cy='96' r='9'/><circle cx='58' cy='93' r='1' fill='${stroke}' stroke='none'/><path d='M63 93a2 1 0 0 1 4 0'/><path d='M57 99a5 4 0 0 0 9 0'/><path d='M28 116l-5 8h4l-3 8'/><path d='M92 148h14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3h-6l-4 4v-4h-4a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3z'/><path d='M186 130l1.6 4.4 4.4 1.6-4.4 1.6-1.6 4.4-1.6-4.4-4.4-1.6 4.4-1.6z'/><circle cx='150' cy='40' r='2.5'/><circle cx='76' cy='226' r='2.5'/><circle cx='216' cy='76' r='2.5'/><circle cx='128' cy='210' r='2.5'/><circle cx='168' cy='150' r='2.5'/></g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};
const TASK_CHAT_WALLPAPER_LIGHT = buildChatWallpaper("rgba(14,144,70,0.16)");
const TASK_CHAT_WALLPAPER_DARK = buildChatWallpaper("rgba(79,195,137,0.10)");

/** Light: dark FAB + white chevrons. Dark: light FAB + dark chevrons (matches inbox ChatBox). */
const taskChatScrollToBottomIconColor = (theme) =>
  theme.palette.mode === "dark"
    ? "rgba(20, 20, 24, 0.92) !important"
    : "rgba(255, 255, 255, 0.95) !important";

const taskChatScrollToBottomFabSx = {
  position: "absolute",
  right: 12,
  bottom: 10,
  zIndex: 2,
  width: 40,
  height: 40,
  padding: 0,
  boxShadow: (theme) =>
    theme.palette.mode === "dark"
      ? "0 2px 10px rgba(0,0,0,0.35)"
      : "0 2px 10px rgba(0,0,0,0.18)",
  backgroundColor: (theme) =>
    theme.palette.mode === "dark"
      ? "rgba(236, 236, 240, 0.98)"
      : "rgba(42, 42, 46, 0.96)",
  border: (theme) =>
    `1px solid ${
      theme.palette.mode === "dark"
        ? "rgba(0,0,0,0.08)"
        : "rgba(255,255,255,0.12)"
    }`,
  "& .MuiSvgIcon-root": {
    color: taskChatScrollToBottomIconColor,
  },
  "&:hover": {
    backgroundColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(250, 250, 252, 1)"
        : "rgba(52, 52, 56, 1)",
    "& .MuiSvgIcon-root": {
      color: taskChatScrollToBottomIconColor,
    },
  },
};

const ACCEPT_TASK_CHAT_MEDIA = taskChatComposerFileAccept();

const ChatDaySeparator = ({ label }) => (
  <Box sx={{ display: "flex", justifyContent: "center", my: 1.25, px: 1 }}>
    <Typography
      component="span"
      variant="caption"
      sx={{
        px: 1.6,
        py: 0.5,
        borderRadius: 999,
        fontWeight: 600,
        fontSize: "0.72rem",
        letterSpacing: 0.03,
        bgcolor: (t) => (t.palette.mode === "dark" ? "#23262b" : "#ffffff"),
        color: (t) =>
          t.palette.mode === "dark" ? "rgba(226, 232, 240, 0.85)" : "text.secondary",
        border: (t) =>
          `1px solid ${
            t.palette.mode === "dark" ? "rgba(148, 163, 184, 0.22)" : "rgba(15, 23, 42, 0.08)"
          }`,
        boxShadow: (t) =>
          t.palette.mode === "dark"
            ? "0 1px 2px rgba(0,0,0,0.45)"
            : "0 1px 3px rgba(15,23,42,0.12)",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const formatShortTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

const mapBucketToChatType = (mediaType) => {
  switch (mediaType) {
    case "images":
      return "image";
    case "videos":
      return "video";
    case "audio":
      return "audio";
    default:
      return "document";
  }
};

const ReceiptTicks = ({ status }) => {
  const read = status === "read";
  const delivered = status === "delivered";
  // Outgoing bubble is the light-green Inbox style, so ticks are dark grey
  // (blue when read) — visible on the light background in both modes.
  const tick = {
    sent: "rgba(0,0,0,0.4)",
    delivered: "rgba(0,0,0,0.5)",
    read: "#34B7F1",
  };
  if (!status || status === "sent") {
    return <DoneIcon sx={{ fontSize: 14, ml: 0.5, color: tick.sent }} />;
  }
  return (
    <DoneAllIcon
      sx={{
        fontSize: 14,
        ml: 0.5,
        color: read ? tick.read : delivered ? tick.delivered : tick.sent,
      }}
    />
  );
};

/** Dense rows for the task overview strip (Bitrix-like compact metadata). */
const TaskOverviewRow = ({ label, value }) => (
  <Box sx={{ display: "flex", gap: 0.6, alignItems: "flex-start", minWidth: 0, py: 0.15 }}>
    <Typography
      component="span"
      color="text.secondary"
      sx={{ flexShrink: 0, fontSize: "0.85rem", lineHeight: 1.4 }}
    >
      {label}:
    </Typography>
    <Typography
      component="span"
      sx={{
        fontWeight: 500,
        fontSize: "0.85rem",
        lineHeight: 1.4,
        flex: 1,
        minWidth: 0,
        wordBreak: "break-word",
        overflowWrap: "anywhere",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

/** Stable fallback so `messages` is not a new `[]` every render (exhaustive-deps / useMemo). */
const EMPTY_TASK_CHAT_MESSAGES = [];

/**
 * Build mention rows from the task form / detail payload (individual team members only).
 * Server-expanded team rosters still come from `mentionMembers`; this fills gaps and matches
 * what the user sees in Owners / Assignees without touching inbox behaviour.
 */
function mentionSeedFromTaskPeople(taskPeople) {
  if (!taskPeople || typeof taskPeople !== "object") return [];
  const { owners = [], assignees = [], participants = [], createdBy } = taskPeople;
  const rows = [];
  const seen = new Set();

  const pushRow = (idRaw, nameRaw) => {
    const id = String(idRaw ?? "").trim();
    const name = String(nameRaw ?? "").trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    rows.push({ id, name: name || "Member" });
  };

  const bumpCreator = (cre) => {
    if (!cre || typeof cre !== "object") return;
    const cid = cre.id ?? cre._id;
    pushRow(cid, cre.name);
  };

  bumpCreator(createdBy);

  const bumpPeople = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const p of arr) {
      if (!p?.id || String(p.id).trim() === "") continue;
      if (String(p.type || "").toLowerCase() === "team") continue;
      pushRow(p.id, p.name);
    }
  };

  bumpPeople(owners);
  bumpPeople(assignees);
  bumpPeople(participants);

  return rows;
}

function mergeTaskChatMentionRoster(apiMembers, taskPeople) {
  const apiList = Array.isArray(apiMembers) ? apiMembers : [];
  const seedList = mentionSeedFromTaskPeople(taskPeople);

  const byId = new Map();
  const order = [];

  const upsert = (entry) => {
    if (!entry?.id) return;
    const id = String(entry.id).trim();
    if (!id) return;
    const name = String(entry.name || "").trim() || "Member";
    if (!byId.has(id)) {
      byId.set(id, { id, name });
      order.push(id);
      return;
    }
    const prev = byId.get(id);
    const keepName = String(prev.name || "").trim();
    if (!keepName || keepName === "Member") {
      prev.name = name;
    }
  };

  for (const m of apiList) upsert(m);
  for (const m of seedList) upsert(m);

  return order.map((id) => byId.get(id)).filter(Boolean);
}

const TaskChatPanel = ({
  taskId,
  memberCount = 0,
  compact = false,
  onClose,
  taskSummary = null,
  taskPeopleForMentions = null,
  readOnly = false,
  onViewTask = null,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const chatWidthRootRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiToggleRef = useRef(null);

  const authUser = useSelector((s) => s.auth?.user || s.userDetails?.userDetails);
  const viewerId = authUser?._id || authUser?.id || "";

  const bucket = useSelector((s) => (taskId ? s.taskChat?.byTaskId?.[taskId] : null));
  const messages = useMemo(() => {
    const arr = bucket?.messages;
    return Array.isArray(arr) ? arr : EMPTY_TASK_CHAT_MESSAGES;
  }, [bucket?.messages]);
  const hasMore = bucket?.hasMore === true;
  const countShown = bucket?.memberCount ?? memberCount ?? 0;

  const [draft, setDraft] = useState("");
  const [mentionState, setMentionState] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [sending, setSending] = useState(false);
  /** Synchronous re-entrancy guard: the `sending` state alone doesn't stop rapid repeated
   * Enter presses from firing multiple sends before a re-render lands (the Enter handler
   * calls handleSend() directly, bypassing the send button's `disabled` prop). */
  const sendingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [membersAnchor, setMembersAnchor] = useState(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [recordingVoice, setRecordingVoice] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const stickToBottomRef = useRef(true);
  const isPrependingRef = useRef(false);
  const scrollRafRef = useRef(false);
  const loadOlderLockRef = useRef(false);
  const lastOlderFetchRef = useRef(0);
  const markReadPendingRef = useRef(false);

  const mentionRoster = useMemo(() => {
    const merged = mergeTaskChatMentionRoster(bucket?.mentionMembers, taskPeopleForMentions);
    const byId = new Map();
    for (const m of merged) {
      if (!m?.id) continue;
      const id = String(m.id);
      if (!byId.has(id)) byId.set(id, m);
    }
    return [...byId.values()];
  }, [bucket?.mentionMembers, taskPeopleForMentions]);

  const mentionNameBySenderId = useMemo(() => {
    const map = new Map();
    for (const m of mentionRoster) {
      if (!m?.id) continue;
      map.set(String(m.id), String(m.name || "").trim() || "Member");
    }
    return map;
  }, [mentionRoster]);

  const viewerDisplayName = useMemo(() => {
    const u = authUser;
    if (!u) return "";
    const full = String(u.name || "").trim();
    if (full) return full;
    const combo = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
    return combo || "";
  }, [authUser]);

  const resolveTaskChatSenderLabel = useCallback(
    (msg, fromMeFlag) => {
      const fromPayload = String(msg?.senderName || "").trim();
      if (fromPayload) return fromPayload;
      const sid = msg?.senderId != null ? String(msg.senderId) : "";
      if (sid && mentionNameBySenderId.has(sid)) {
        return mentionNameBySenderId.get(sid);
      }
      if (fromMeFlag && viewerDisplayName) return viewerDisplayName;
      return sid ? "Member" : "Member";
    },
    [mentionNameBySenderId, viewerDisplayName]
  );

  const visibleMessages = useMemo(() => {
    if (!taskSummary) return messages;
    return messages.filter((m) => !(m.kind === "system" && m.action === "created_banner"));
  }, [messages, taskSummary]);

  const unreadIncomingSignature = useMemo(() => {
    if (!viewerId) return "";
    const viewerStr = String(viewerId);
    return visibleMessages
      .filter((m) => {
        if (!m || m.kind === "system") return false;
        const senderStr = m.senderId != null ? String(m.senderId) : "";
        if (!senderStr || senderStr === viewerStr) return false;
        const readReceipts = m.readReceipts || {};
        return !readReceipts[viewerStr];
      })
      .map((m) => String(m.id))
      .join("|");
  }, [visibleMessages, viewerId]);

  const filteredMentions = useMemo(() => {
    if (!mentionState) return [];
    const q = (mentionState.query || "").toLowerCase().trim();
    const base = mentionRoster;
    if (!q) return base.slice(0, 32);
    return base.filter((m) => (m.name || "").toLowerCase().includes(q)).slice(0, 32);
  }, [mentionState, mentionRoster]);

  useEffect(() => {
    setMentionIndex(0);
  }, [mentionState?.query, filteredMentions.length]);

  useLayoutEffect(() => {
    const el = chatWidthRootRef.current;
    if (!el) return undefined;
    const apply = () => {
      el.style.setProperty("--task-chat-width", `${el.offsetWidth}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [compact, taskId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        emojiToggleRef.current &&
        !emojiToggleRef.current.contains(event.target)
      ) {
        setEmojiPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setDraft("");
    setMentionState(null);
    setSelectedImages([]);
    setSelectedVideos([]);
    setSelectedDocuments([]);
    setRecordedAudio(null);
    setRecordingVoice(false);
    setEmojiPickerOpen(false);
  }, [taskId]);

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    stickToBottomRef.current = true;
    lastOlderFetchRef.current = 0;
    loadOlderLockRef.current = false;
    setShowScrollToBottom(false);
    setLoading(true);
    dispatch(fetchTaskChatMessages({ taskId, limit: TASK_CHAT_PAGE_SIZE, offset: 0 }))
      .unwrap()
      .then(() => {
        if (!cancelled) dispatch(markTaskChatReadThunk({ taskId }));
      })
      .catch((err) => {
        if (!cancelled && !isSubscriptionBlockedError(err)) toast.error(err || "Unable to load task chat");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, taskId]);

  useEffect(() => {
    if (!taskId || !unreadIncomingSignature) return undefined;

    let cancelled = false;
    const markRead = () => {
      if (cancelled || document.hidden || markReadPendingRef.current) return;
      markReadPendingRef.current = true;
      dispatch(markTaskChatReadThunk({ taskId })).finally(() => {
        markReadPendingRef.current = false;
      });
    };

    const t = setTimeout(markRead, 150);
    const handleFocus = () => markRead();
    const handleVisibilityChange = () => {
      if (!document.hidden) markRead();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch, taskId, unreadIncomingSignature]);

  const scrollListToBottom = useCallback((smooth) => {
    const el = listRef.current;
    if (!el) return;
    const scroll = () => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    };
    requestAnimationFrame(() => {
      scroll();
      requestAnimationFrame(scroll);
    });
  }, []);

  const updateScrollFab = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      setShowScrollToBottom(false);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const hasOverflow = scrollHeight > clientHeight + 2;
    const scrolledUp = distanceFromBottom > SCROLL_BOTTOM_THRESHOLD_PX;
    setShowScrollToBottom(hasOverflow && scrolledUp);

    const nearBottom = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
    stickToBottomRef.current = nearBottom;
  }, []);

  const onListScroll = useCallback(() => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = true;
    requestAnimationFrame(() => {
      scrollRafRef.current = false;
      updateScrollFab();
      const el = listRef.current;
      if (!el || !taskId || loading || loadingOlder || !hasMore || loadOlderLockRef.current) return;
      if (messages.length === 0 || el.scrollTop > 100) return;
      if (Date.now() - lastOlderFetchRef.current < 500) return;
      lastOlderFetchRef.current = Date.now();
      loadOlderLockRef.current = true;
      const prevH = el.scrollHeight;
      const prevTop = el.scrollTop;
      isPrependingRef.current = true;
      setLoadingOlder(true);
      dispatch(fetchOlderTaskChatMessages({ taskId }))
        .unwrap()
        .catch(() => {})
        .finally(() => {
          setLoadingOlder(false);
          requestAnimationFrame(() => {
            const node = listRef.current;
            if (node) {
              const delta = node.scrollHeight - prevH;
              node.scrollTop = prevTop + delta;
            }
            isPrependingRef.current = false;
            loadOlderLockRef.current = false;
            updateScrollFab();
          });
        });
    });
  }, [dispatch, taskId, loading, loadingOlder, hasMore, messages.length, updateScrollFab]);

  const tailMessageId = visibleMessages[visibleMessages.length - 1]?.id;

  /** After initial load when task chat opens, land on latest messages. */
  useEffect(() => {
    if (!taskId || loading) return;
    stickToBottomRef.current = true;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      scrollListToBottom(false);
      updateScrollFab();
    };
    const t = setTimeout(run, 120);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [taskId, loading, scrollListToBottom, updateScrollFab]);

  useEffect(() => {
    if (!listRef.current || loading || isPrependingRef.current) return;
    if (!stickToBottomRef.current) return;
    const t = setTimeout(() => scrollListToBottom(false), 80);
    return () => clearTimeout(t);
  }, [loading, tailMessageId, visibleMessages.length, taskId, scrollListToBottom]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || !taskId) return undefined;
    const ro = new ResizeObserver(() => {
      updateScrollFab();
    });
    ro.observe(el);
    updateScrollFab();
    return () => ro.disconnect();
  }, [taskId, loading, visibleMessages.length, updateScrollFab]);

  const handleScrollToBottomClick = useCallback(() => {
    stickToBottomRef.current = true;
    setShowScrollToBottom(false);
    scrollListToBottom(true);
    requestAnimationFrame(() => updateScrollFab());
  }, [scrollListToBottom, updateScrollFab]);

  const updateMentionHint = (value, caret) => {
    const slice = value.slice(0, caret);
    const at = slice.lastIndexOf("@");
    if (at === -1) {
      setMentionState(null);
      return;
    }
    const frag = slice.slice(at + 1);
    if (/[\s\n]/.test(frag)) {
      setMentionState(null);
      return;
    }
    setMentionState({ start: at, query: frag });
  };

  const selectMention = (member) => {
    if (!mentionState) return;
    const caret =
      typeof inputRef.current?.selectionStart === "number"
        ? inputRef.current.selectionStart
        : draft.length;
    const before = draft.slice(0, mentionState.start);
    const tail = draft.slice(caret);
    const insert = `@${String(member.name || "Member").replace(/\s+/g, " ")} `;
    const next = before + insert + tail;
    setDraft(next);
    setMentionState(null);

    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el?.setSelectionRange) return;
      const pos = before.length + insert.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const mkTempId = () => `temp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  const uploadAndSendOneFile = async (file) => {
    const uploaded = await dispatch(uploadTaskChatMedia({ taskId, file })).unwrap();
    await dispatch(
      sendTaskChatMessage({
        taskId,
        message: uploaded.url,
        messageType: mapBucketToChatType(uploaded.mediaType),
        tempId: mkTempId(),
        documentFileName:
          uploaded.mediaType === "documents"
            ? uploaded.originalName || file.name
            : undefined,
      })
    ).unwrap();
  };

  const handleComposerPick = (e) => {
    const input = e?.target;
    const picked =
      input && input.files && input.files.length ? Array.from(input.files) : [];
    if (input && typeof input.value === "string") {
      input.value = "";
    }
    if (!picked.length) return;
    if (!taskId) {
      toast.error("Task chat attachments are available once this task exists.");
      return;
    }

    const nextImg = [...selectedImages];
    const nextVid = [...selectedVideos];
    const nextDocs = [...selectedDocuments];
    let rejected = false;
    picked.forEach((file) => {
      const kind = classifyTaskChatComposerFile(file);
      if (!kind) {
        rejected = true;
        return;
      }
      if (kind === "image") nextImg.push(file);
      else if (kind === "video") nextVid.push(file);
      else if (kind === "audio") {
        toast.error("Use the microphone to send voice clips.");
      } else nextDocs.push(file);
    });
    if (rejected) toast.error("One or more files are not supported for task chat.");

    setSelectedImages(nextImg);
    setSelectedVideos(nextVid);
    setSelectedDocuments(nextDocs);
  };

  const toggleEmojiPicker = async (ev) => {
    ev.stopPropagation();
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
    setEmojiPickerOpen((o) => !o);
  };

  const handleSend = async () => {
    if (!taskId) return;
    if (sendingRef.current) return;
    const text = draft.trim();
    const hasAttachments =
      selectedImages.length +
        selectedVideos.length +
        selectedDocuments.length >
      0;
    const hasVoice = !!recordedAudio;
    if (!text && !hasAttachments && !hasVoice) return;

    // ── subscription pre-check ──
    const { blocked: _blocked, message: _blockMsg } = await checkBeforeOutbound(getAuthToken);
    if (_blocked) {
      toast.error(_blockMsg, { id: 'subscription-blocked', duration: 5000 });
      return; // draft stays in textbox (setDraft only called on success)
    }

    sendingRef.current = true;
    setSending(true);
    try {
      if (text) {
        await dispatch(
          sendTaskChatMessage({
            taskId,
            message: text,
            messageType: "text",
            tempId: mkTempId(),
          })
        ).unwrap();
        setDraft("");
        setMentionState(null);
      }

      for (const file of selectedImages) {
        await uploadAndSendOneFile(file);
      }
      for (const file of selectedVideos) {
        await uploadAndSendOneFile(file);
      }
      for (const file of selectedDocuments) {
        await uploadAndSendOneFile(file);
      }

      if (recordedAudio) {
        await uploadAndSendOneFile(recordedAudio);
        setRecordedAudio(null);
        setRecordingVoice(false);
      }

      setSelectedImages([]);
      setSelectedVideos([]);
      setSelectedDocuments([]);

      stickToBottomRef.current = true;
      setShowScrollToBottom(false);
      setTimeout(() => scrollListToBottom(true), 50);
    } catch (e) {
      if (!isSubscriptionBlockedError(typeof e === "string" ? e : e?.message)) {
        toast.error(typeof e === "string" ? e : e?.message || "Message not sent");
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const onDraftKeyDown = (e) => {
    if (mentionState && filteredMentions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((i) => Math.min(filteredMentions.length - 1, i + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionState(null);
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        selectMention(filteredMentions[mentionIndex]);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const pendingFileCount =
    selectedImages.length + selectedVideos.length + selectedDocuments.length;
  const hasPendingMedia = pendingFileCount > 0;
  const voicePanelBlocksSend = recordingVoice && !recordedAudio;
  const sendDisabled =
    sending ||
    voicePanelBlocksSend ||
    (!draft.trim() && !hasPendingMedia && !recordedAudio);

  /** Inbox SVGs use fill/stroke `#fff`; invert for light-mode task chat footer (`background.paper`). */
  const composerIconBtnSx = useMemo(
    () => ({
      flexShrink: 0,
      color:
        theme.palette.mode === "light"
          ? theme.palette.primary.main
          : theme.palette.common.white,
      "& svg path": {
        fill: "currentColor",
      },
      "&:disabled": {
        color:
          theme.palette.mode === "light"
            ? theme.palette.action.disabled
            : "rgba(255,255,255,0.35)",
      },
    }),
    [theme.palette.mode, theme.palette.primary.main, theme.palette.action.disabled, theme.palette.common.white]
  );

  const recorderIconBtnSx = useMemo(
    () => ({
      flexShrink: 0,
      color:
        theme.palette.mode === "light"
          ? theme.palette.primary.main
          : theme.palette.common.white,
      "& svg path": {
        fill: "none",
        stroke: "currentColor",
      },
      "&:disabled": {
        color:
          theme.palette.mode === "light"
            ? theme.palette.action.disabled
            : "rgba(255,255,255,0.35)",
      },
    }),
    [theme.palette.mode, theme.palette.primary.main, theme.palette.action.disabled, theme.palette.common.white]
  );

  const sendBtnSx = useMemo(
    () => ({
      flexShrink: 0,
      ...(sendDisabled
        ? {}
        : {
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : theme.palette.grey[100],
          }),
      "&.Mui-disabled": {
        color:
          theme.palette.mode === "light"
            ? theme.palette.action.disabled
            : "rgba(255,255,255,0.35)",
        opacity: 1,
      },
    }),
    [
      sendDisabled,
      theme.palette.mode,
      theme.palette.primary.main,
      theme.palette.grey,
      theme.palette.action.disabled,
    ]
  );

  if (!taskId) {
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: "background.default",
          borderLeft: (t) => `1px solid ${t.palette.divider}`,
          display: "flex",
          flexDirection: "column",
          minHeight: 200,
          flex: compact ? 1 : undefined,
          minWidth: compact ? { xs: 0, sm: 320 } : undefined,
          width: compact ? "100%" : undefined,
        }}
      >
        {onClose && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5, mt: -0.5 }}>
            <IconButton onClick={onClose} aria-label="Close task" edge="end" size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ fontSize: "1rem" }}>
            Task chat is available after the task is created.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={chatWidthRootRef}
      sx={{
        ...(compact
          ? {
              flex: "1 1 0%",
              minWidth: { xs: 0, sm: 280 },
              width: "100%",
              maxWidth: "100%",
            }
          : {
              width: { xs: 300, sm: 380 },
              flexShrink: 0,
            }),
        borderLeft: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        minHeight: compact ? { xs: 320, sm: "100%" } : "100%",
        alignSelf: compact ? "stretch" : undefined,
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 1.25,
          pb: 1,
          minHeight: 48,
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          flexShrink: 0,
        }}
      >
        {onViewTask ? (
          /* ── Task Chats Box: big avatar left, task name + members in column to the right ── */
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1, minWidth: 0 }}>
            {taskSummary?.title && (
              <Avatar
                sx={{
                  width: 44, height: 44, fontSize: "1.35rem", flexShrink: 0, mt: 0.15,
                  bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
                  border: (t) => `1px solid ${t.palette.divider}`,
                }}
              >
                {nameToEmoji(taskSummary.title)}
              </Avatar>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {taskSummary?.title && (
                <Typography noWrap fontWeight={700} sx={{ fontSize: { xs: "1.0rem", sm: "1.08rem" }, lineHeight: 1.35 }}>
                  {taskSummary.title}
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "nowrap", mt: 0.25 }}>
                <Typography
                  color="text.secondary"
                  onClick={(e) => countShown > 0 && setMembersAnchor(e.currentTarget)}
                  sx={{
                    fontSize: "0.88rem",
                    lineHeight: 1,
                    cursor: countShown > 0 ? "pointer" : "default",
                    "&:hover": countShown > 0 ? { color: "primary.main", textDecoration: "underline" } : {},
                  }}
                >
                  {countShown} member{countShown !== 1 ? "s" : ""}
                </Typography>
                <Typography
                  onClick={() => onViewTask(taskId)}
                  sx={{ fontSize: "0.88rem", fontWeight: 600, color: "primary.main", cursor: "pointer", lineHeight: 1, "&:hover": { textDecoration: "underline" } }}
                >
                  View task <span style={{ fontSize: "1.1em" }}>›</span>
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          /* ── Task Form Details Box: bordered chat icon + "Task chat" + members ── */
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: "50%", flexShrink: 0, mt: 0.1,
                bgcolor: (t) => t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.1)"
                  : alpha(t.palette.primary.main, 0.12),
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <ForumOutlinedIcon sx={{ fontSize: "1.3rem", color: "primary.main" }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={700} sx={{ fontSize: { xs: "1.05rem", sm: "1.125rem" }, lineHeight: 1.35 }}>
                Task chat
              </Typography>
              <Typography
                color="text.secondary"
                onClick={(e) => countShown > 0 && setMembersAnchor(e.currentTarget)}
                sx={{
                  fontSize: { xs: "0.88rem", sm: "0.9rem" },
                  lineHeight: 1.35,
                  mt: 0.2,
                  cursor: countShown > 0 ? "pointer" : "default",
                  "&:hover": countShown > 0 ? { color: "primary.main", textDecoration: "underline" } : {},
                }}
              >
                {countShown} member{countShown !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Members popover */}
        <Popover
          open={Boolean(membersAnchor)}
          anchorEl={membersAnchor}
          onClose={() => setMembersAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { mt: 0.5, minWidth: 220, maxWidth: 280, maxHeight: 340, borderRadius: 1.5 } }}
        >
          {(bucket?.mentionMembers || []).length === 0 ? (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">No members found</Typography>
            </Box>
          ) : (
            (bucket?.mentionMembers || []).map((m) => (
              <Box key={m.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1 }}>
                <Avatar
                  sx={{
                    width: 34, height: 34, fontSize: "1rem",
                    bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
                    border: (t) => `1px solid ${t.palette.divider}`,
                  }}
                >
                  {nameToEmoji(m.name || "?")}
                </Avatar>
                <Typography noWrap sx={{ fontWeight: 500, fontSize: "0.95rem" }}>
                  {m.name || "Member"}
                </Typography>
              </Box>
            ))
          )}
        </Popover>
        {onClose && (
          <IconButton onClick={onClose} aria-label="Close task" edge="end" sx={{ mt: -0.25, flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {loadingOlder && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 4,
              height: 3,
              borderRadius: 0,
            }}
          />
        )}
        <Box
          ref={listRef}
          onScroll={onListScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            px: 1.5,
            py: 1,
            // WhatsApp-style doodle wallpaper over a soft brand-green tinted gradient.
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#121214" : "#e6f1ea",
            backgroundImage: (theme) =>
              theme.palette.mode === "dark"
                ? `${TASK_CHAT_WALLPAPER_DARK}, linear-gradient(160deg, #17191d 0%, #121214 55%, #141518 100%)`
                : `${TASK_CHAT_WALLPAPER_LIGHT}, linear-gradient(160deg, #d6ecdd 0%, #e7f2ea 50%, #deedef 100%)`,
            backgroundSize: "240px 240px, auto",
            backgroundRepeat: "repeat, no-repeat",
            scrollbarWidth: "thin",
            scrollbarColor: (theme) =>
              theme.palette.mode === "dark"
                ? "#555 #1a1a1a"
                : "#888 #f1f1f1",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#555" : "#888",
              borderRadius: "10px",
            },
          }}
        >
        {taskSummary && taskId && (
          <Paper
            elevation={0}
            sx={{
              mb: 1,
              p: { xs: 1, sm: 1.25 },
              borderRadius: 1,
              border: (t) => `1px solid ${t.palette.divider}`,
              borderLeft: 3,
              borderLeftColor: "primary.main",
              bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : "background.paper"),
              boxShadow: (t) =>
                t.palette.mode === "dark" ? "0 1px 2px rgba(0,0,0,0.25)" : "0 1px 2px rgba(15,23,42,0.06)",
            }}
          >
            <Typography
              fontWeight={600}
              sx={{
                fontSize: "0.95rem",
                lineHeight: 1.3,
                wordBreak: "break-word",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {taskSummary.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.2, mb: 0.5, fontSize: "0.75rem", lineHeight: 1.3 }}
            >
              Task overview
            </Typography>
            <Divider sx={{ my: 0.35, opacity: 0.85 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "minmax(0,1fr) minmax(0,1fr)" },
                columnGap: 1.25,
                rowGap: 0.4,
              }}
            >
              <TaskOverviewRow label="Type" value={taskSummary.type} />
              <TaskOverviewRow label="Status" value={taskSummary.status} />
              <TaskOverviewRow label="Priority" value={taskSummary.priority} />
              <TaskOverviewRow label="Deadline" value={taskSummary.deadline} />
              <TaskOverviewRow label="Created by" value={taskSummary.createdBy} />
              <TaskOverviewRow label="Created at" value={taskSummary.createdAt} />
              <TaskOverviewRow label="Owner(s)" value={taskSummary.owners} />
              <TaskOverviewRow label="Assignee(s)" value={taskSummary.assignees} />
              {taskSummary.recurrence ? (
                <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}>
                  <TaskOverviewRow label="Recurrence" value={taskSummary.recurrence} />
                </Box>
              ) : null}
            </Box>
          </Paper>
        )}

        {loading && (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={26} />
          </Box>
        )}
        {!loading && visibleMessages.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, pt: taskSummary ? 0 : 2 }}>
            No messages yet. Say hello to your team.
          </Typography>
        )}
        {!loading &&
          visibleMessages.map((msg, idx) => {
            const prev = idx > 0 ? visibleMessages[idx - 1] : null;
            const dayLabel = formatDateSeparator(msg.timestamp);
            const prevDay = prev ? formatDateSeparator(prev.timestamp) : null;
            const showDaySep = !prevDay || prevDay !== dayLabel;

            if (msg.kind === "system") {
              const lines = (msg.summary || "").split("\n").filter(Boolean);
              const title =
                msg.action === "created_banner"
                  ? "Task summary"
                  : msg.action === "activity"
                    ? msg.actorName || "Activity"
                    : msg.actorName || "Activity";
              const isCreatedBanner = msg.action === "created_banner";
              const actorLabel =
                typeof msg.actorName === "string" && msg.actorName.trim().length > 0
                  ? msg.actorName.trim()
                  : !isCreatedBanner
                    ? title
                    : null;
              const isActivityLog = msg.action === "activity";
              // Recurrence cron tags these with actorName "Recurring automation".
              const isAutomation =
                !isCreatedBanner &&
                /^recurring automation$/i.test((msg.actorName || "").trim());
              const actionText =
                lines.length > 0
                  ? isActivityLog
                    ? lines.map((ln) => ln.trim()).join("\n")
                    : lines.map((ln) => ln.trim()).join(" ").replace(/\s+/g, " ").trim()
                  : (msg.summary || "")
                      .split("\n")
                      .map((ln) => ln.trim())
                      .join(isActivityLog ? "\n" : " ")
                      .replace(isActivityLog ? /\n{3,}/g : /\s+/g, isActivityLog ? "\n\n" : " ")
                      .trim();
              const isLight = theme.palette.mode === "light";
              // CRM-style accent: only for regular human activity (banner = green, automation = amber).
              const activityCat =
                !isCreatedBanner && !isAutomation ? classifyTaskActivity(actionText) : null;
              const ActivityIcon = activityCat ? activityCat.Icon : null;
              // Each distinct change is its own line — render as a bulleted list
              // when there's more than one so multi-field updates stay scannable.
              const actionLines = actionText
                ? actionText.split("\n").map((s) => s.trim()).filter(Boolean)
                : [];
              return (
                <React.Fragment key={msg.id}>
                  {showDaySep && dayLabel ? <ChatDaySeparator label={dayLabel} /> : null}
                  <Box sx={{ my: 1.6, display: "flex", justifyContent: "center", width: "100%" }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        width: "fit-content",
                        maxWidth: "92%",
                        boxSizing: "border-box",
                        // Opaque surface so text stays readable over the wallpaper.
                        // Recurring automation events get a light-yellow surface to stand apart.
                        bgcolor: (t) =>
                          isAutomation
                            ? t.palette.mode === "dark"
                              ? "#2c2616"
                              : "#fef8e6"
                            : t.palette.mode === "dark"
                              ? "#23262b"
                              : "#ffffff",
                        borderRadius: 1.5,
                        px: 1.15,
                        py: 0.7,
                        boxShadow: (t) =>
                          t.palette.mode === "dark"
                            ? "0 1px 2px rgba(0,0,0,0.45)"
                            : "0 1px 3px rgba(15,23,42,0.12)",
                        border: (t) =>
                          isAutomation
                            ? `1px solid ${
                                t.palette.mode === "dark"
                                  ? "rgba(245, 158, 11, 0.4)"
                                  : "rgba(245, 158, 11, 0.35)"
                              }`
                            : isLight
                              ? `1px solid rgba(15, 23, 42, 0.08)`
                              : `1px solid rgba(148, 163, 184, 0.22)`,
                        // Task summary = the anchor card: brand-green left accent.
                        ...(isCreatedBanner && {
                          borderLeft: (t) => `3px solid ${t.palette.primary.main}`,
                        }),
                        // Activity events: CRM-style colour accent by event type.
                        ...(activityCat && {
                          borderLeft: `3px solid ${activityCat.accent}`,
                        }),
                      }}
                    >
                      {activityCat ? (
                        // USER UPDATE: two-column notification — icon gutter + aligned
                        // content column (bold actor + time on one line, detail below).
                        <Box sx={{ display: "flex", gap: 0.9, alignItems: "flex-start" }}>
                          {ActivityIcon ? (
                            <ActivityIcon
                              sx={{ fontSize: 18, mt: "1px", flexShrink: 0, color: activityCat.accent }}
                            />
                          ) : null}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                justifyContent: "space-between",
                                columnGap: 1,
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 700,
                                  lineHeight: 1.3,
                                  color: activityCat.accent,
                                  wordBreak: "break-word",
                                }}
                              >
                                {actorLabel || "Member"}
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  flexShrink: 0,
                                  fontSize: "0.65rem",
                                  lineHeight: 1,
                                  whiteSpace: "nowrap",
                                  color: (t) =>
                                    t.palette.mode === "dark" ? "rgba(226, 232, 240, 0.78)" : "text.secondary",
                                  opacity: 0.92,
                                }}
                              >
                                {formatShortTime(msg.timestamp)}
                              </Typography>
                            </Box>
                            {actionLines.length > 1 ? (
                              <Box
                                component="div"
                                sx={{
                                  mt: 0.4,
                                  display: "flex",
                                  flexDirection: "column",
                                  rowGap: 0.4,
                                }}
                              >
                                {actionLines.map((line, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      columnGap: 0.7,
                                      fontSize: "0.85rem",
                                      lineHeight: 1.45,
                                      color: (t) =>
                                        isLight ? t.palette.grey[800] : "rgba(226, 232, 240, 0.85)",
                                      wordBreak: "break-word",
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        flexShrink: 0,
                                        mt: "1px",
                                        fontSize: "0.7rem",
                                        lineHeight: 1.45,
                                        color: activityCat.accent,
                                      }}
                                    >
                                      ●
                                    </Box>
                                    <Box component="span" sx={{ minWidth: 0 }}>
                                      {renderActivityText(line)}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography
                                component="div"
                                variant="body2"
                                sx={{
                                  mt: 0.3,
                                  fontSize: "0.85rem",
                                  lineHeight: 1.5,
                                  whiteSpace: "pre-line",
                                  textAlign: "left",
                                  color: (t) => (isLight ? t.palette.grey[800] : "rgba(226, 232, 240, 0.85)"),
                                  fontWeight: 400,
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {renderActivityText(actionText)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "wrap",
                              alignItems: "center",
                              columnGap: 0.75,
                              rowGap: 0.35,
                              minWidth: 0,
                              maxWidth: "100%",
                            }}
                          >
                            {isCreatedBanner ? (
                              <AssignmentOutlinedIcon
                                sx={{ fontSize: 17, flexShrink: 0, color: "primary.main" }}
                              />
                            ) : isAutomation ? (
                              <AutorenewIcon
                                sx={{ fontSize: 17, flexShrink: 0, color: "#d97706" }}
                              />
                            ) : null}
                            {isCreatedBanner ? (
                              <Chip
                                label="Task summary"
                                size="small"
                                sx={{
                                  height: 22,
                                  maxWidth: 220,
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  flexShrink: 0,
                                  bgcolor: (t) =>
                                    t.palette.mode === "dark"
                                      ? "rgba(14, 144, 70, 0.22)"
                                      : "rgba(14, 144, 70, 0.10)",
                                  color: "primary.main",
                                  border: (t) =>
                                    `1px solid ${
                                      t.palette.mode === "dark" ? "rgba(14,144,70,0.5)" : "rgba(14,144,70,0.35)"
                                    }`,
                                  "& .MuiChip-label": { px: 0.9, overflow: "hidden", textOverflow: "ellipsis" },
                                }}
                              />
                            ) : (
                              <Typography
                                component="span"
                                sx={{
                                  flexShrink: 0,
                                  fontSize: "0.78rem",
                                  fontWeight: 700,
                                  lineHeight: 1.3,
                                  color: (t) => (t.palette.mode === "dark" ? "#fbbf24" : "#b45309"),
                                }}
                              >
                                {actorLabel || "Recurring automation"}
                              </Typography>
                            )}
                          </Box>
                          {(actionText || !isCreatedBanner) && (
                            <Typography
                              component="div"
                              variant="body2"
                              sx={{
                                mt: 0.4,
                                width: "100%",
                                fontSize: "0.85rem",
                                lineHeight: 1.45,
                                whiteSpace: isActivityLog ? "pre-line" : "normal",
                                textAlign: "left",
                                color: (t) => (isLight ? t.palette.grey[800] : "rgba(226, 232, 240, 0.85)"),
                                fontWeight: 400,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {renderActivityText(actionText, isAutomation)}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              alignSelf: "flex-end",
                              mt: 0.35,
                              fontSize: "0.65rem",
                              lineHeight: 1,
                              whiteSpace: "nowrap",
                              color: (t) =>
                                t.palette.mode === "dark" ? "rgba(226, 232, 240, 0.78)" : "text.secondary",
                              opacity: 0.92,
                            }}
                          >
                            {formatShortTime(msg.timestamp)}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </React.Fragment>
              );
            }

            const fromMe = !!(msg.fromMe || (viewerId && msg.senderId && String(viewerId) === String(msg.senderId)));
            const light = theme.palette.mode === "light";
            // Outgoing bubbles use the Inbox light-green style (#D9FDD3) in both modes.
            const senderLabel = resolveTaskChatSenderLabel(msg, fromMe);

            const bubbleBg = !fromMe
              ? (t) => (t.palette.mode === "dark" ? t.palette.grey[800] : t.palette.grey[100])
              : () => "#D9FDD3";

            const bubbleColor = !fromMe ? "text.primary" : "#111b21";

            return (
              <React.Fragment key={msg.id}>
                {showDaySep && dayLabel ? <ChatDaySeparator label={dayLabel} /> : null}
                <Stack
                  direction={fromMe ? "row-reverse" : "row"}
                  sx={{
                    mb: 1,
                    alignSelf: fromMe ? "flex-end" : "flex-start",
                    maxWidth: "100%",
                    alignItems: "flex-start",
                    gap: { xs: 0.5, sm: 0.75 },
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      pt: 0.125,
                      minWidth: { xs: 40, sm: 44 },
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: isXs ? 32 : 34, height: isXs ? 32 : 34,
                        fontSize: isXs ? "1rem" : "1.1rem",
                        bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
                        border: (t) => `1px solid ${t.palette.divider}`,
                      }}
                    >
                      {nameToEmoji(senderLabel)}
                    </Avatar>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: fromMe ? "flex-end" : "flex-start",
                      gap: 0.35,
                      maxWidth: { xs: "calc(100% - 44px)", sm: "min(88%, calc(100% - 48px))" },
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                        color: "text.primary",
                        lineHeight: 1.25,
                        px: 0.125,
                      }}
                    >
                      {senderLabel}
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        px: 1.1,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: bubbleBg,
                        color: bubbleColor,
                        boxShadow: fromMe ? "0 1px 2px rgba(0,0,0,0.14)" : 1,
                        border: (t) => (!fromMe && light ? `1px solid ${t.palette.divider}` : "none"),
                      }}
                    >
                      <TaskChatMessageMedia msg={msg} fromMe={fromMe} />
                      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5} sx={{ mt: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.7rem",
                            opacity: fromMe ? 0.65 : 0.75,
                            color: fromMe ? "rgba(0,0,0,0.5) !important" : undefined,
                          }}
                        >
                          {formatShortTime(msg.timestamp)}
                        </Typography>
                        {fromMe && <ReceiptTicks status={msg.status} />}
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </React.Fragment>
            );
          })}
        </Box>

        {!loading && visibleMessages.length > 0 && (
          <Zoom in={showScrollToBottom} unmountOnExit>
            <IconButton
              aria-label="Scroll to bottom"
              onClick={handleScrollToBottomClick}
              size="small"
              sx={taskChatScrollToBottomFabSx}
            >
              <KeyboardDoubleArrowDownIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Zoom>
        )}
      </Box>

      {readOnly ? (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(255,255,255,0.04)"
                : "rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontStyle: "italic", textAlign: "center" }}
          >
            You can view this chat but cannot send messages.
          </Typography>
        </Box>
      ) : (
      <Box
        sx={{
          p: 1.5,
          borderTop: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: "background.paper",
          position: "relative",
        }}
      >
        {mentionState && filteredMentions.length > 0 && (
          <Paper
            elevation={6}
            sx={{
              position: "absolute",
              left: 8,
              right: 72,
              bottom: "calc(100% - 8px)",
              maxHeight: 280,
              overflow: "auto",
              zIndex: 20,
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <List disablePadding sx={{ py: 0 }}>
              {filteredMentions.map((m, idx) => (
                <ListItem key={m.id} disablePadding>
                  <ListItemButton
                    selected={idx === mentionIndex}
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      selectMention(m);
                    }}
                    sx={{
                      py: 1,
                      alignItems: "center",
                      "&.Mui-selected": { bgcolor: "action.selected" },
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        sx={{
                          width: 36, height: 36, fontSize: "1.1rem",
                          bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
                          border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                      >
                        {nameToEmoji(m.name || "Member")}
                      </Avatar>
                    </ListItemAvatar>
                    <Typography
                      component="span"
                      variant="body1"
                      sx={{ fontSize: "0.9375rem", lineHeight: 1.35, fontWeight: 500 }}
                    >
                      {m.name}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {emojiPickerOpen && (
          <Box
            ref={emojiPickerRef}
            sx={{
              position: "absolute",
              bottom: "100%",
              left: 8,
              mb: 1,
              zIndex: (t) => t.zIndex.modal,
              borderRadius: 1,
              overflow: "hidden",
              boxShadow: (t) =>
                t.palette.mode === "dark"
                  ? "0px 4px 10px rgba(0, 0, 0, 0.3)"
                  : "0px 4px 10px rgba(0, 0, 0, 0.1)",
              bgcolor: "background.paper",
              ...(isXs
                ? {
                    width: "min(100%, calc(100vw - 24px))",
                    maxHeight:
                      "min(360px, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 200px))",
                    display: "flex",
                    flexDirection: "column",
                    "& > div": { width: "100%", minHeight: 0, flex: "1 1 auto" },
                    "& em-emoji-picker": {
                      width: "100% !important",
                      height: "100% !important",
                      maxHeight: "100% !important",
                      boxSizing: "border-box",
                    },
                  }
                : {}),
            }}
          >
            <Picker
              data={data}
              onEmojiSelect={(emoji) => setDraft((prev) => prev + emoji.native)}
              theme={theme.palette.mode}
              dynamicWidth={isXs}
              previewPosition={isXs ? "none" : "bottom"}
              {...(isXs ? { emojiButtonSize: 30, emojiSize: 20 } : {})}
            />
          </Box>
        )}

        {hasPendingMedia && (
          <Box
            sx={{
              mb: 1,
              borderRadius: 1,
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: (t) =>
                t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[50],
              overflow: "hidden",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                px: 1.25,
                py: 0.75,
                fontWeight: 600,
                color: "text.secondary",
                bgcolor: (t) => (t.palette.mode === "dark" ? "grey.900" : "grey.100"),
                borderBottom: (t) => `1px solid ${t.palette.divider}`,
              }}
            >
              {pendingFileCount} file{pendingFileCount !== 1 ? "s" : ""} selected
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                p: 1,
              }}
            >
            {selectedImages.map((image, index) => (
              <Box position="relative" key={`img-${image.name}-${image.size}-${index}`}>
                <Box
                  component="img"
                  src={URL.createObjectURL(image)}
                  alt=""
                  sx={{ height: 72, width: 72, objectFit: "cover", borderRadius: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setSelectedImages((p) => p.filter((x) => x !== image))}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    bgcolor: "background.paper",
                    p: 0.25,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
            {selectedVideos.map((video, index) => (
              <Box position="relative" key={`vid-${video.name}-${video.size}-${index}`}>
                <Box
                  component="video"
                  src={URL.createObjectURL(video)}
                  sx={{ height: 72, width: 72, objectFit: "cover", borderRadius: 1 }}
                  muted
                  playsInline
                  onLoadedData={(ev) => ev.target.pause()}
                />
                <IconButton
                  size="small"
                  onClick={() => setSelectedVideos((p) => p.filter((x) => x !== video))}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    bgcolor: "background.paper",
                    p: 0.25,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
            {selectedDocuments.map((doc, index) => (
              <Box position="relative" key={`doc-${doc.name}-${doc.size}-${index}`}>
                <Box
                  sx={{
                    height: 72,
                    width: 72,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    bgcolor: "background.default",
                  }}
                >
                  <PictureAsPdfIcon sx={{ color: "text.secondary" }} />
                  <Typography variant="caption" noWrap sx={{ maxWidth: 68, px: 0.5, textAlign: "center" }}>
                    {doc.name}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setSelectedDocuments((p) => p.filter((x) => x !== doc))}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    bgcolor: "background.paper",
                    p: 0.25,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
            </Box>
          </Box>
        )}

        {voicePanelBlocksSend && !hasPendingMedia && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            Recording… finish or cancel to send other messages.
          </Typography>
        )}

        <input
          ref={galleryInputRef}
          type="file"
          hidden
          multiple
          accept={ACCEPT_TASK_CHAT_MEDIA}
          onChange={handleComposerPick}
        />

        <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ minWidth: 0 }}>
          {!(recordingVoice || recordedAudio) && (
            <>
              <IconButton
                ref={emojiToggleRef}
                size="small"
                onClick={toggleEmojiPicker}
                disabled={sending}
                aria-label="Emoji"
                sx={composerIconBtnSx}
              >
                <EmojiButton style={{ width: 20, height: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                disabled={sending}
                aria-label="Attach files"
                onClick={() => galleryInputRef.current?.click()}
                sx={composerIconBtnSx}
              >
                <GalleryButton style={{ width: 20, height: 20 }} />
              </IconButton>
            </>
          )}

          {(recordingVoice || recordedAudio) && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <AudioRecorder
                onRecordingComplete={(file) => {
                  setRecordedAudio(file);
                  setRecordingVoice(false);
                }}
                onCancel={() => {
                  setRecordedAudio(null);
                  setRecordingVoice(false);
                }}
                onRecordingReset={() => {
                  setRecordedAudio(null);
                  setRecordingVoice(true);
                }}
              />
            </Box>
          )}

          {!(recordingVoice || recordedAudio) && (
            <TextField
              size="small"
              fullWidth
              multiline
              maxRows={4}
              placeholder="Write a message… (@ to mention)"
              value={draft}
              inputRef={inputRef}
              onChange={(e) => {
                const v = e.target.value;
                const caret =
                  typeof e.target.selectionStart === "number" ? e.target.selectionStart : v.length;
                setDraft(v);
                updateMentionHint(v, caret);
              }}
              onSelect={(e) => {
                updateMentionHint(e.target.value, e.target.selectionStart ?? draft.length);
              }}
              onKeyDown={onDraftKeyDown}
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiInputBase-root": {
                  fontSize: "0.9375rem",
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.9375rem",
                  lineHeight: 1.45,
                },
              }}
            />
          )}

          {!(recordingVoice || recordedAudio) && (
            <IconButton
              size="small"
              onClick={() => setRecordingVoice(true)}
              disabled={sending}
              aria-label="Record voice"
              sx={recorderIconBtnSx}
            >
              <RecordingButton style={{ width: 20, height: 20 }} />
            </IconButton>
          )}

          <IconButton size="small" onClick={handleSend} disabled={sendDisabled} sx={sendBtnSx} aria-label="Send">
            {sending ? (
              <CircularProgress
                size={20}
                sx={{
                  color:
                    theme.palette.mode === "light"
                      ? theme.palette.primary.main
                      : theme.palette.grey[100],
                }}
              />
            ) : (
              <SendIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Stack>
      </Box>
      )}
    </Box>
  );
};

export default TaskChatPanel;
